// @ts-check
// Mensagens do WhatsApp: separadores de data, balões recebidos à esquerda e enviados à direita,
// hora com status (relógio, um tique, dois tiques, azul quando lida, vermelho quando falhou),
// citação da mensagem respondida e anexos. Desde a 2.0.0 (contrato 09 de campanhas de WhatsApp, seção 4), o selo da
// mensagem em massa ("Campanha: nome", "Fluxo: nome") e o da confirmação automática de saída.
import { html } from 'htm/preact';
import { Fragment } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { LightButton } from '../../ui/button.js';
import { formatDaySeparator, formatFullDateTime, formatTime, getDayKey, getMessageKindLabel, getMessageSnippet } from '../../lib/conversation-format.js';
import { MessageAttachmentView } from './message-attachments.js';
import { navigate } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/conversations.js').ConversationMessage} ConversationMessage */

/** Distância do fim da lista abaixo da qual novas mensagens rolam a tela automaticamente. */
const STICK_TO_BOTTOM_THRESHOLD = 120;
const GROUP_WINDOW_MILLISECONDS = 5 * 60_000;

/**
 * @param {{ message: ConversationMessage }} props
 */
export function MessageStatus({ message }) {
  if (message.direction !== 'OUTGOING') return null;
  const label = t(`conversations.status.${message.status}`);
  switch (message.status) {
    case 'PENDING':
      return html`<span class="message-status" data-status="PENDING" title=${label} aria-label=${label}><${Icon} name="IconClock" size=${14} /></span>`;
    case 'SENT':
      return html`<span class="message-status" data-status="SENT" title=${label} aria-label=${label}><${Icon} name="IconCheck" size=${14} /></span>`;
    case 'DELIVERED':
      return html`<span class="message-status" data-status="DELIVERED" title=${label} aria-label=${label}><${Icon} name="IconChecks" size=${14} /></span>`;
    case 'READ':
      return html`<span class="message-status" data-status="READ" title=${label} aria-label=${label}><${Icon} name="IconChecks" size=${14} /></span>`;
    case 'FAILED': {
      const reason = message.errorMessage ? t('conversations.failedWithReason', { reason: message.errorMessage }) : t('conversations.failedNoReason');
      return html`<span class="message-status" data-status="FAILED" title=${reason} aria-label=${reason}><${Icon} name="IconAlertCircle" size=${14} /></span>`;
    }
    default:
      return null;
  }
}

/**
 * @param {ConversationMessage} message
 */
function getMessageKind(message) {
  if (message.kind) return message.kind;
  const attachment = message.attachments?.[0];
  if (!attachment) return 'TEXT';
  return attachment.isVoiceNote ? 'VOICE_NOTE' : attachment.kind;
}

/**
 * @param {{ quoted: ConversationMessage | undefined, contactName: string }} props
 */
function ReplyQuote({ quoted, contactName }) {
  if (!quoted) {
    return html`<div class="message-quote" data-missing="true"><span class="message-quote-text">${t('conversations.reply.unavailable')}</span></div>`;
  }
  const author = quoted.direction === 'OUTGOING' ? quoted.sentBy?.name ?? t('timeline.you') : contactName;
  const text = getMessageSnippet(quoted.text, null) || getMessageKindLabel(getMessageKind(quoted));
  return html`<div class="message-quote" data-direction=${quoted.direction}>
    <span class="message-quote-author">${author}</span>
    <span class="message-quote-text">${text}</span>
  </div>`;
}

/**
 * Última reação de cada lado por mensagem alvo, como no WhatsApp (reação nova substitui, vazia remove).
 * @param {ConversationMessage[]} messages
 * @returns {Map<string, { emoji: string, direction: string }[]>}
 */
export function collectReactions(messages) {
  /** @type {Map<string, Map<string, string | null>>} */
  const latest = new Map();
  for (const message of messages) {
    if (!message.reaction || !message.replyToMessageId) continue;
    const byDirection = latest.get(message.replyToMessageId) ?? new Map();
    byDirection.set(message.direction, message.reaction.emoji);
    latest.set(message.replyToMessageId, byDirection);
  }
  const result = new Map();
  for (const [targetId, byDirection] of latest) {
    const reactions = [...byDirection].filter(([, emoji]) => emoji).map(([direction, emoji]) => ({ emoji: /** @type {string} */ (emoji), direction }));
    if (reactions.length > 0) result.set(targetId, reactions);
  }
  return result;
}

/**
 * Selo da bolha: de que campanha ou fluxo veio a mensagem em massa (com link só para quem abre aquela tela) e a
 * resposta automática de saída. O servidor manda `bulk` e `isAutomaticReply` só quando há.
 * @param {{ message: ConversationMessage & { bulk?: { kind: string, campaignId: string | null, campaignName: string | null, flowId: string | null, flowName: string | null } | null,
 *   isAutomaticReply?: boolean } }} props
 */
export function MessageBulkBadge({ message }) {
  // 3.0.0: mensagem do bot ("Bot: <nome>") e aviso automático do atendimento (origens BOT e SYSTEM_NOTICE)
  const origin = /** @type {{ origin?: string | null, bot?: { botId: string, botName: string } | null }} */ (message).origin;
  if (origin === 'BOT') {
    const botName = /** @type {any} */ (message).bot?.botName;
    return html`<span class="message-bulk-badge" data-kind="bot"><${Icon} name="IconRobot" size=${12} />${botName ? t('conversations.botBadge', { name: botName }) : t('conversations.botBadgeUnnamed')}</span>`;
  }
  if (origin === 'SYSTEM_NOTICE') {
    return html`<span class="message-bulk-badge" data-kind="systemNotice"><${Icon} name="IconInfoCircle" size=${12} />${t('conversations.systemNoticeBadge')}</span>`;
  }
  if (message.isAutomaticReply) {
    return html`<span class="message-bulk-badge" data-kind="automatic"><${Icon} name="IconRobot" size=${12} />${t('conversations.automaticReply')}</span>`;
  }
  const bulk = message.bulk;
  if (!bulk) return null;
  const isFlow = bulk.kind === 'FLOW';
  const label = bulk.kind === 'WHATSAPP_CAMPAIGN_TEST'
    ? t('conversations.campaignTestBadge')
    : isFlow
      ? t('conversations.flowBadge', { name: bulk.flowName || t('conversations.bulkUnnamed') })
      : t('conversations.campaignBadge', { name: bulk.campaignName || t('conversations.bulkUnnamed') });
  const targetId = isFlow ? bulk.flowId : bulk.campaignId;
  const href = targetId && (isFlow ? hasCurrentPermissionFlag('WORKFLOWS') : hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS'))
    ? `/campaigns/${isFlow ? 'flows' : 'whatsapp'}/${encodeURIComponent(targetId)}`
    : null;
  const icon = html`<${Icon} name=${isFlow ? 'IconRoute' : 'IconSpeakerphone'} size=${12} />`;
  return href
    ? html`<a class="message-bulk-badge" data-kind=${bulk.kind} href=${href} onClick=${(/** @type {MouseEvent} */ event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(href);
      }}>${icon}${label}</a>`
    : html`<span class="message-bulk-badge" data-kind=${bulk.kind}>${icon}${label}</span>`;
}

/**
 * Bolha de uma mensagem do WhatsApp, usada pela conversa e pela Caixa de atendimento (3.0.0). `leading` vai antes da
 * bolha na linha (a caixa de seleção do "Dividir" na Caixa), `className` soma à linha e `isSelected` marca a linha.
 * @param {{ message: ConversationMessage, quoted?: ConversationMessage, contactName: string, showSender: boolean, isFirstInGroup: boolean,
 *   reactions?: { emoji: string, direction: string }[], leading?: any, className?: string, isSelected?: boolean }} props
 */
export function MessageBubble({ message, quoted, contactName, showSender, isFirstInGroup, reactions, leading = null, className = '', isSelected = false }) {
  const isOutgoing = message.direction === 'OUTGOING';
  const attachments = message.attachments ?? [];
  const isSticker = attachments.length === 1 && attachments[0].kind === 'STICKER' && !message.text;
  const hasMedia = attachments.some((attachment) => attachment.kind === 'IMAGE' || attachment.kind === 'VIDEO');
  const meta = html`<span class="message-meta">
    <time datetime=${message.sentAt} title=${formatFullDateTime(message.sentAt)}>${formatTime(message.sentAt)}</time>
    <${MessageStatus} message=${message} />
  </span>`;
  return html`<div
    class=${className ? `message-row ${className}` : 'message-row'}
    data-direction=${message.direction}
    data-first-in-group=${isFirstInGroup ? 'true' : undefined}
    data-message-id=${message.id}
    data-selected=${isSelected ? 'true' : undefined}
  >
    ${leading}
    <div class="message-bubble" data-sticker=${isSticker ? 'true' : undefined} data-media=${hasMedia ? 'true' : undefined} data-status=${message.status}>
      ${showSender && isOutgoing && message.sentBy ? html`<span class="message-sender">${message.sentBy.name}</span>` : null}
      ${isOutgoing ? html`<${MessageBulkBadge} message=${message} />` : null}
      ${message.replyToMessageId ? html`<${ReplyQuote} quoted=${quoted} contactName=${contactName} />` : null}
      ${attachments.map((attachment) => html`<div key=${attachment.fileId} class="message-attachment"><${MessageAttachmentView} attachment=${attachment} /></div>`)}
      ${/** @type {any} */ (message).isRestricted
        // 3.0.0: lugar de uma mensagem de atendimento restrito que o papel não vê, sem o conteúdo
        ? html`<div class="message-text message-restricted" title=${t('conversations.restrictedMessageDescription')}><${Icon} name="IconLock" size=${12} /> ${t('conversations.restrictedMessage')}${meta}</div>`
        : message.text ? html`<div class="message-text">${message.text}${meta}</div>` : html`<div class="message-meta-row">${meta}</div>`}
      ${reactions?.length
        ? html`<div class="message-reactions" title=${reactions.map((reaction) => `${reaction.direction === 'OUTGOING' ? t('timeline.you') : contactName}: ${reaction.emoji}`).join('\n')}>
            ${reactions.map((reaction) => html`<span key=${reaction.direction}>${reaction.emoji}</span>`)}
          </div>`
        : null}
    </div>
    ${isOutgoing && message.status === 'FAILED'
      ? html`<div class="message-failed-note">${message.errorMessage ? t('conversations.failedWithReason', { reason: message.errorMessage }) : t('conversations.failedNoReason')}</div>`
      : null}
  </div>`;
}

/**
 * @param {{ messages: ConversationMessage[], contactName: string, hasOlder: boolean, isLoadingOlder: boolean, onLoadOlder: () => void, footer?: any }} props
 */
export function WhatsappMessageList({ messages, contactName, hasOlder, isLoadingOlder, onLoadOlder, footer }) {
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const previousRef = useRef({ lastId: /** @type {string | null} */ (null), count: 0, scrollHeight: 0, firstId: /** @type {string | null} */ (null) });
  const byId = new Map(messages.map((message) => [message.id, message]));
  const reactionsByMessageId = collectReactions(messages);

  // Mantém a leitura no fim quando chegam mensagens novas e preserva a posição ao carregar as antigas.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const previous = previousRef.current;
    const lastMessage = messages[messages.length - 1];
    const firstMessage = messages[0];
    const distanceFromBottom = previous.scrollHeight - element.scrollTop - element.clientHeight;
    if (previous.firstId && firstMessage && firstMessage.id !== previous.firstId && lastMessage?.id === previous.lastId) {
      element.scrollTop += element.scrollHeight - previous.scrollHeight;
    } else if (!previous.lastId || (lastMessage && lastMessage.id !== previous.lastId && (distanceFromBottom < STICK_TO_BOTTOM_THRESHOLD || lastMessage.direction === 'OUTGOING'))) {
      element.scrollTop = element.scrollHeight;
    }
    previousRef.current = { lastId: lastMessage?.id ?? null, count: messages.length, scrollHeight: element.scrollHeight, firstId: firstMessage?.id ?? null };
  }, [messages]);

  // Imagens carregadas depois aumentam a altura; quem estava no fim continua no fim.
  const handleMediaLoad = () => {
    const element = scrollRef.current;
    if (!element) return;
    const previous = previousRef.current;
    if (previous.scrollHeight - element.scrollTop - element.clientHeight < STICK_TO_BOTTOM_THRESHOLD) element.scrollTop = element.scrollHeight;
    previousRef.current = { ...previous, scrollHeight: element.scrollHeight };
  };

  /** @type {any[]} */
  const items = [];
  let previousDayKey = '';
  /** @type {ConversationMessage | null} */
  let previousMessage = null;
  for (const message of messages) {
    if (message.reaction && message.replyToMessageId && byId.has(message.replyToMessageId)) continue;
    const dayKey = getDayKey(message.sentAt);
    if (dayKey !== previousDayKey) {
      items.push(html`<div key=${`day-${dayKey}`} class="message-day-separator"><span>${formatDaySeparator(message.sentAt)}</span></div>`);
      previousDayKey = dayKey;
      previousMessage = null;
    }
    const isFirstInGroup =
      !previousMessage ||
      previousMessage.direction !== message.direction ||
      previousMessage.sentBy?.workspaceMemberId !== message.sentBy?.workspaceMemberId ||
      new Date(message.sentAt).getTime() - new Date(previousMessage.sentAt).getTime() > GROUP_WINDOW_MILLISECONDS;
    items.push(html`<${MessageBubble}
      key=${message.id}
      message=${message}
      quoted=${message.replyToMessageId ? byId.get(message.replyToMessageId) : undefined}
      contactName=${contactName}
      showSender=${isFirstInGroup}
      isFirstInGroup=${isFirstInGroup}
      reactions=${reactionsByMessageId.get(message.id)}
    />`);
    previousMessage = message;
  }

  return html`<div class="whatsapp-messages" ref=${scrollRef} onLoadCapture=${handleMediaLoad}>
    <div class="whatsapp-messages-inner">
      ${hasOlder
        ? html`<div class="messages-load-older"><${LightButton} title=${t('conversations.loadOlder')} icon="IconChevronUp" disabled=${isLoadingOlder} onClick=${onLoadOlder} /></div>`
        : null}
      <${Fragment}>${items}<//>
      ${footer ?? null}
    </div>
  </div>`;
}
